import { motion } from 'framer-motion';
import {
  BatteryCharging,
  Sun,
  Sparkles,
  Building2,
  Zap,
  IndianRupee,
  BarChart3,
  RefreshCcw,
  Clock,
  MapPin,
  CloudRain,
  ArrowRight,
  Sunrise,
  MessageCircle,
  Download,
} from 'lucide-react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatINR, formatKWh, formatNumber } from '@/lib/format';
import { useCountUp } from '@/lib/useCountUp';

const CHART_MARGIN = { top: 8, right: 8, left: 0, bottom: 24 };
const CHART_BAR_RADIUS = [8, 8, 0, 0];
const CHART_CURSOR = { fill: 'hsl(214 100% 97% / 0.7)' };
const CHART_TOOLTIP_STYLE = {
  borderRadius: '12px',
  border: '1px solid hsl(214 32% 91%)',
  background: 'white',
  boxShadow: '0 8px 24px rgba(15,23,42,0.08)',
};
const RESULTS_MOTION_INITIAL = { opacity: 0, y: 10 };
const RESULTS_MOTION_ANIMATE = { opacity: 1, y: 0 };
const RESULTS_MOTION_TRANSITION = { duration: 0.35 };

function slotColor(type) {
  if (type === 'peak') return 'hsl(var(--chart-4))';
  if (type === 'off_peak') return 'hsl(var(--chart-2))';
  if (type === 'solar_day') return 'hsl(var(--chart-3))';
  return 'hsl(var(--chart-1))';
}

function slotTypeLabel(type) {
  if (type === 'peak') return 'Peak';
  if (type === 'off_peak') return 'Off-peak';
  if (type === 'solar_day') return 'Solar day';
  return 'Normal';
}

// UrjaPod's sales line, in the digits-only form wa.me expects (91 = India).
const SALES_WHATSAPP = '918154070298';

/** The analysis as a WhatsApp message the customer can send without editing.
 *
 * Written to be read on a phone: short lines, no tables, WhatsApp's own *bold*.
 * Every figure is one the results page is already showing, so the sales team
 * and the customer are looking at the same numbers.
 */
function buildCommercialsMessage(submission) {
  const analysis = submission?.analysis || {};
  const extraction = submission?.extraction || {};
  const customer = submission?.customer || {};
  const solar = analysis.solar || {};
  const bess = analysis.bess || {};
  const chosen = analysis.options?.[analysis.options?.recommended];

  const line = (label, value) => (value ? `${label}: ${value}` : null);
  const money = (n) => (n ? formatINR(n, { compact: false, decimals: 0 }) : null);

  const annual = analysis.total_annual_savings_rs || 0;
  const monthly = chosen?.net_monthly_savings_rs;

  const parts = [
    'Hi UrjaPod, please send me commercials for this solar + battery proposal.',
    '',
    '*My bill*',
    line('Consumer', extraction.consumer_name || customer.company_name),
    line('Utility', [extraction.discom || extraction.utility_name, analysis.state].filter(Boolean).join(' · ')),
    line('Category', analysis.consumer_type || extraction.consumer_type_inferred),
    line(
      'Billing month',
      [extraction.billing_month, extraction.billing_days && `${extraction.billing_days} days`]
        .filter(Boolean)
        .join(' · '),
    ),
    line('Consumption', extraction.total_consumption_kwh && formatKWh(extraction.total_consumption_kwh)),
    line('Contract demand', extraction.contract_demand_kva && `${formatNumber(extraction.contract_demand_kva)} kVA`),
    '',
    '*Recommended system*',
    line('Solar', solar.kwp_dc && `${formatNumber(solar.kwp_dc)} kWp DC (${formatNumber(solar.kw_ac)} kW AC)`),
    line('Battery', bess.kwh_capacity ? `${formatNumber(bess.kwh_capacity)} kWh` : 'Not required'),
    line(
      'Sizing basis',
      analysis.options?.recommended === 'tod_optimum'
        ? 'Best return from my TOD slots'
        : analysis.options?.recommended === 'regulatory_sizing'
          ? `${analysis.state || 'State'} storage regulation`
          : null,
    ),
    '',
    '*Estimated savings*',
    line('Per year', money(annual)),
    line('Per month', money(monthly)),
    '',
    line('Reference', submission?.job_id),
    line('My name', customer.person_name),
  ];

  return parts.filter((p) => p !== null).join('\n');
}

function whatsappLink(submission) {
  return `https://wa.me/${SALES_WHATSAPP}?text=${encodeURIComponent(buildCommercialsMessage(submission))}`;
}

const KpiHero = ({ value, submission }) => {
  const animated = useCountUp(value, { duration: 1100 });
  return (
    <Card
      className="relative overflow-hidden rounded-2xl border-border/70 bg-card p-6 sm:p-8"
      data-testid="total-annual-savings"
    >
      <div
        className="absolute inset-y-0 right-0 hidden w-1/2 opacity-70 sm:block"
        style={{
          background:
            'radial-gradient(400px 220px at 100% 50%, hsl(214 100% 96% / 0.9), transparent 70%)',
        }}
        aria-hidden
      />
      <div className="relative flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
            <Sparkles className="h-4 w-4 text-primary" />
            Estimated annual savings with Solar + BESS
          </div>
          <div
            className="mt-2 font-display text-4xl font-semibold tracking-tight text-foreground tabular sm:text-5xl lg:text-6xl"
            data-testid="animated-kpi"
          >
            {/* Printing captures whatever the count-up is showing at that
              * instant, so a customer who hits print early gets a headline
              * saving that is simply wrong. Print the settled figure. */}
            <span className="print:hidden">{formatINR(animated, { compact: false, decimals: 0 })}</span>
            <span className="hidden print:inline">{formatINR(value, { compact: false, decimals: 0 })}</span>
          </div>
          <div className="mt-1 text-sm text-muted-foreground">
            <span className="tabular">{formatINR(value, { compact: true, decimals: 2 })}</span>
            <span> per year · based on your bill and state tariff</span>
          </div>
        </div>
        <div className="flex flex-col gap-3 sm:items-end">
          <Button
            asChild
            size="lg"
            className="w-full rounded-xl sm:w-auto"
            data-testid="contact-commercials-button"
          >
            <a
              href={whatsappLink(submission)}
              target="_blank"
              rel="noopener noreferrer"
            >
              <MessageCircle className="mr-2 h-4 w-4" />
              Contact me for commercials
            </a>
          </Button>
          <div className="flex flex-wrap gap-2 sm:justify-end">
            <Badge className="border border-primary/20 bg-accent text-accent-foreground">
              Physics-based sizing
            </Badge>
            <Badge className="border border-primary/20 bg-accent text-accent-foreground">
              TOD-aware
            </Badge>
            <Badge className="border border-primary/20 bg-accent text-accent-foreground">
              India tariffs
            </Badge>
          </div>
        </div>
      </div>
    </Card>
  );
};

const BillSummary = ({ extraction, analysis }) => {
  if (!extraction) return null;
  const rows = [
    ['Utility / DISCOM', extraction.utility_name || extraction.discom || '-'],
    ['State (auto-detected)', analysis?.state || extraction.state_detected || '-'],
    ['Consumer', extraction.consumer_name || '-'],
    ['Consumer number', extraction.consumer_number || '-'],
    ['Consumer category', extraction.consumer_category || '-'],
    ['Consumer type (auto-detected)', analysis?.consumer_type || extraction.consumer_type_inferred || '-'],
    ['Billing month', extraction.billing_month || '-'],
    ['Billing days', extraction.billing_days || '-'],
    [
      'Contract demand',
      extraction.contract_demand_kva ? `${formatNumber(extraction.contract_demand_kva)} kVA` : '-',
    ],
    [
      'Sanctioned load',
      extraction.sanctioned_load_kw ? `${formatNumber(extraction.sanctioned_load_kw)} kW` : '-',
    ],
    [
      'Total consumption',
      extraction.total_consumption_kwh ? formatKWh(extraction.total_consumption_kwh) : '-',
    ],
    [
      'Existing rooftop solar',
      extraction.existing_solar_kwp ? `${extraction.existing_solar_kwp} kWp` : 'None',
    ],
  ];
  return (
    <Card className="rounded-2xl border-border/70 bg-card p-6" data-testid="bill-summary-table">
      <div className="mb-3 flex items-center gap-2">
        <Building2 className="h-5 w-5 text-primary" />
        <h3 className="font-display text-base font-semibold">Bill summary</h3>
      </div>
      <div className="divide-y divide-border/60">
        {rows.map(([k, v]) => (
          <div
            key={k}
            className="flex items-center justify-between gap-4 py-2.5 text-sm"
            data-testid={`bill-row-${k.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
          >
            <span className="text-muted-foreground">{k}</span>
            <span className="text-right font-medium text-foreground tabular">{v}</span>
          </div>
        ))}
      </div>
    </Card>
  );
};

const TodConsumptionTable = ({ extraction, analysis }) => {
  const slots = extraction?.tod_slots || [];
  if (slots.length === 0) return null;
  const cfg = analysis?.tod_slots_config || {};

  const rows = slots.map((s) => {
    const cfgKey = `slot_${s.slot_number}`;
    const meta = cfg[cfgKey] || {};
    return {
      slot_label: s.slot_label || meta.label || `Slot ${s.slot_number}`,
      slot_number: s.slot_number,
      window: `${s.start_time || meta.start || ''} – ${s.end_time || meta.end || ''}`,
      type: meta.type || 'normal',
      kwh: Number(s.consumption_kwh || 0),
    };
  });

  const total = rows.reduce((acc, r) => acc + r.kwh, 0);

  return (
    <Card className="rounded-2xl border-border/70 bg-card p-6" data-testid="tod-consumption-table">
      <div className="mb-3 flex items-center gap-2">
        <Clock className="h-5 w-5 text-primary" />
        <h3 className="font-display text-base font-semibold">TOD consumption breakdown</h3>
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Slot</TableHead>
            <TableHead>Time window</TableHead>
            <TableHead>Type</TableHead>
            <TableHead className="text-right">Consumption</TableHead>
            <TableHead className="text-right">Share</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={`${r.slot_number}-${r.slot_label}`}>
              <TableCell>
                <div className="flex items-center gap-2">
                  <span
                    className="h-2.5 w-2.5 rounded-full"
                    style={{ background: slotColor(r.type) }}
                  />
                  <span className="font-medium">{r.slot_label}</span>
                </div>
              </TableCell>
              <TableCell className="text-muted-foreground tabular">{r.window}</TableCell>
              <TableCell>
                <Badge
                  variant="outline"
                  className="border-border/70 bg-accent/60 text-xs text-accent-foreground"
                >
                  {slotTypeLabel(r.type)}
                </Badge>
              </TableCell>
              <TableCell className="text-right font-medium tabular">{formatKWh(r.kwh)}</TableCell>
              <TableCell className="text-right text-muted-foreground tabular">
                {total > 0 ? `${((r.kwh / total) * 100).toFixed(1)}%` : '-'}
              </TableCell>
            </TableRow>
          ))}
          <TableRow>
            <TableCell colSpan={3} className="font-semibold">
              Total
            </TableCell>
            <TableCell className="text-right font-semibold tabular">{formatKWh(total)}</TableCell>
            <TableCell className="text-right font-semibold">100%</TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </Card>
  );
};

const TariffTable = ({ tariff, computed }) => {
  if (!tariff && !computed) return null;
  const rows = [
    ['Energy charge', tariff?.energy_charge_rate_rs_per_kwh, '₹/kWh'],
    ['Wheeling charge', tariff?.wheeling_charge_rate_rs_per_kwh, '₹/kWh'],
    ['FAC', tariff?.fac_rate_rs_per_kwh, '₹/kWh'],
    ['Sales tax', tariff?.sales_tax_rate_rs_per_kwh, '₹/kWh'],
    ['Electricity duty', tariff?.electricity_duty_percent, '%'],
    ['Fixed / demand charge', tariff?.fixed_demand_charge_rate, tariff?.fixed_demand_charge_unit || ''],
    ['TOD solar-day rebate', computed?.tod3_rebate_rs_per_kwh, '₹/kWh'],
    ['TOD peak adder', computed?.tod4_adder_rs_per_kwh, '₹/kWh'],
    ['Effective off-peak tariff', computed?.offpeak_tariff_rs_per_kwh, '₹/kWh'],
    ['Effective peak tariff', computed?.peak_tariff_rs_per_kwh, '₹/kWh'],
  ].filter(([, v]) => v !== null && v !== undefined && v !== 0);

  if (rows.length === 0) return null;
  return (
    <Card className="rounded-2xl border-border/70 bg-card p-6" data-testid="tariff-table">
      <div className="mb-3 flex items-center gap-2">
        <IndianRupee className="h-5 w-5 text-primary" />
        <h3 className="font-display text-base font-semibold">Tariff components</h3>
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Component</TableHead>
            <TableHead className="text-right">Rate</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map(([label, value, unit]) => (
            <TableRow key={label}>
              <TableCell className="text-muted-foreground">{label}</TableCell>
              <TableCell className="text-right font-medium tabular">
                {typeof value === 'number' ? formatNumber(value, 3) : value} {unit}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
};

const TodChart = ({ extraction, analysis }) => {
  const cfg = analysis?.tod_slots_config || {};
  const data = (extraction?.tod_slots || []).map((s) => {
    const key = `slot_${s.slot_number}`;
    const meta = cfg[key] || {};
    return {
      name: s.slot_label || `Slot ${s.slot_number}`,
      window: `${s.start_time || meta.start || ''}–${s.end_time || meta.end || ''}`,
      kwh: Number(s.consumption_kwh || 0),
      type: meta.type || 'normal',
      label: meta.label || s.label,
    };
  });
  if (data.length === 0) return null;
  return (
    <Card className="rounded-2xl border-border/70 bg-card p-6" data-testid="tod-consumption-chart">
      <div className="mb-3 flex items-center gap-2">
        <BarChart3 className="h-5 w-5 text-primary" />
        <h3 className="font-display text-base font-semibold">TOD consumption chart</h3>
      </div>
      <div className="h-64 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={CHART_MARGIN}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(214 32% 91% / 0.7)" />
            <XAxis dataKey="name" stroke="hsl(215 16% 35%)" fontSize={12} tickLine={false} axisLine={false} />
            <YAxis stroke="hsl(215 16% 35%)" fontSize={12} tickLine={false} axisLine={false} width={70} />
            <Tooltip
              cursor={CHART_CURSOR}
              contentStyle={CHART_TOOLTIP_STYLE}
              labelFormatter={(_, payload) => {
                const p = payload?.[0]?.payload;
                return p ? `${p.name} · ${p.window} · ${p.label || slotTypeLabel(p.type)}` : '';
              }}
              formatter={(v) => [`${formatNumber(v)} kWh`, 'Consumption']}
            />
            <Legend content={<TodLegend />} />
            <Bar dataKey="kwh" radius={CHART_BAR_RADIUS}>
              {data.map((d) => (
                <Cell key={`bar-cell-${d.name}-${d.window}`} fill={slotColor(d.type)} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
};

const TodLegend = () => (
  <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
    <span className="flex items-center gap-1.5">
      <span className="h-2.5 w-2.5 rounded-full" style={{ background: 'hsl(var(--chart-2))' }} />
      Off-peak
    </span>
    <span className="flex items-center gap-1.5">
      <span className="h-2.5 w-2.5 rounded-full" style={{ background: 'hsl(var(--chart-3))' }} />
      Solar day
    </span>
    <span className="flex items-center gap-1.5">
      <span className="h-2.5 w-2.5 rounded-full" style={{ background: 'hsl(var(--chart-1))' }} />
      Normal
    </span>
    <span className="flex items-center gap-1.5">
      <span className="h-2.5 w-2.5 rounded-full" style={{ background: 'hsl(var(--chart-4))' }} />
      Peak
    </span>
  </div>
);

const SolarCard = ({ solar, existingSolar }) => {
  if (!solar) return null;
  return (
    <Card
      className="rounded-2xl border-border/70 bg-card p-6"
      data-testid="solar-recommendation-card"
    >
      <div className="mb-2 flex items-center gap-2">
        <Sun className="h-5 w-5 text-primary" />
        <h3 className="font-display text-base font-semibold">Solar recommendation</h3>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <div className="text-xs uppercase tracking-wider text-muted-foreground">kWp (DC)</div>
          <div className="font-display text-2xl font-semibold tabular" data-testid="solar-kwp-dc">
            {formatNumber(solar.kwp_dc)}
          </div>
        </div>
        <div>
          <div className="text-xs uppercase tracking-wider text-muted-foreground">kW (AC)</div>
          <div className="font-display text-2xl font-semibold tabular" data-testid="solar-kw-ac">
            {formatNumber(solar.kw_ac)}
          </div>
        </div>
        <div>
          <div className="text-xs uppercase tracking-wider text-muted-foreground">Annual generation</div>
          <div className="font-display text-lg font-semibold tabular">
            {formatKWh(solar.annual_generation_kwh)}
          </div>
        </div>
        <div>
          <div className="text-xs uppercase tracking-wider text-muted-foreground">Daily units offset</div>
          <div className="font-display text-lg font-semibold tabular">
            {formatKWh(solar.daily_offset_tod3_kwh)}
          </div>
        </div>
      </div>
      {existingSolar > 0 && (
        <p className="mt-4 rounded-lg border border-primary/20 bg-accent p-3 text-xs text-accent-foreground">
          You have existing solar of {existingSolar} kWp — the number above is total recommended
          capacity for full offset.
        </p>
      )}
      <div className="mt-4 border-t border-border/60 pt-4">
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Estimated annual solar savings</span>
          <span className="font-semibold tabular" data-testid="solar-annual-savings">
            {formatINR(solar.annual_savings_rs)}
          </span>
        </div>
      </div>
    </Card>
  );
};

const BessCard = ({ bess }) => {
  if (!bess) return null;
  return (
    <Card className="rounded-2xl border-border/70 bg-card p-6" data-testid="bess-recommendation-card">
      <div className="mb-2 flex items-center gap-2">
        <BatteryCharging className="h-5 w-5 text-primary" />
        <h3 className="font-display text-base font-semibold">Battery (BESS) recommendation</h3>
      </div>
      {!bess.eligible ? (
        <div
          className="rounded-lg border border-warning/30 bg-warning/10 p-4 text-sm text-foreground"
          data-testid="bess-not-eligible"
        >
          {bess.message}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="text-xs uppercase tracking-wider text-muted-foreground">kWh capacity</div>
              <div className="font-display text-2xl font-semibold tabular" data-testid="bess-kwh">
                {formatNumber(bess.kwh_capacity)}
              </div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-muted-foreground">Shifted / day</div>
              <div className="font-display text-2xl font-semibold tabular">
                {formatKWh(bess.shifted_per_day_kwh, 1)}
              </div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-muted-foreground">Peak slot</div>
              <div className="font-display text-lg font-semibold tabular">Slot {bess.peak_slot}</div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-muted-foreground">Peak tariff</div>
              <div className="font-display text-lg font-semibold tabular">
                {formatINR(bess.peak_tariff_rs_per_kwh, { decimals: 2 })} /kWh
              </div>
            </div>
          </div>
          <div className="mt-4 border-t border-border/60 pt-4">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Annual BESS savings</span>
              <span className="font-semibold tabular" data-testid="bess-annual-savings">
                {formatINR(bess.annual_savings_rs)}
              </span>
            </div>
            <div className="mt-2 flex flex-wrap gap-2 text-xs text-muted-foreground">
              <span>RTE {(bess.rte * 100).toFixed(0)}%</span>
              <span>·</span>
              <span>DOD {(bess.dod * 100).toFixed(0)}%</span>
              <span>·</span>
              <span>
                {bess.requirement?.mandated
                  ? `Regulatory minimum ${formatNumber(bess.requirement.required_kwh ?? 0)} kWh`
                  : 'Battery sized from your TOD pattern'}
              </span>
            </div>
          </div>
        </>
      )}
    </Card>
  );
};

const RainyMonthBanner = ({ note }) => (
  <div
    className="flex items-start gap-3 rounded-2xl border border-amber-300/60 bg-amber-50 p-4 text-sm text-amber-900"
    data-testid="rainy-month-warning"
  >
    <CloudRain className="mt-0.5 h-5 w-5 flex-none text-amber-600" />
    <div>
      <div className="font-semibold">Monsoon-month data notice</div>
      <div className="mt-0.5 text-amber-900/80">
        {note ||
          'Solar data will not be consistent and accurate for analysis due to rainy weather during June/July/August/September. Please re-upload a bill from October–May for the most accurate result.'}
      </div>
    </div>
  </div>
);

const BASIS_LABELS = {
  tod_optimum: 'Best value from your time-of-day pattern',
  regulatory_sizing: 'Standard sizing formula for your state',
  flat_tariff_estimate: 'Flat-tariff estimate (no time-of-day data on this bill)',
  insufficient_data: 'Not enough detail on this bill',
};

const BREAKDOWN_LABELS = {
  solar_self_consumption: 'Solar used on site',
  battery_peak_shift: 'Battery shifting peak units',
  surplus_export_income: 'Surplus exported to grid',
  grid_support_charge: 'Grid support charge',
  added_demand_charge: 'Extra demand charge',
};

const SizingOptions = ({ analysis }) => {
  const options = analysis?.options;
  if (!options) return null;

  const chosen = options.recommended;
  const optimum = options.tod_optimum;
  const regulatory = options.regulatory_sizing;
  const flat = options.flat_tariff_estimate;
  const active = optimum || flat || regulatory;
  if (!active) return null;

  const breakdown = active.savings_breakdown_monthly_rs || {};
  const rows = Object.entries(BREAKDOWN_LABELS)
    .filter(([key]) => Number(breakdown[key] || 0) !== 0)
    .map(([key, label]) => ({ key, label, value: Number(breakdown[key] || 0) }));

  const alternatives = [
    optimum && chosen !== 'tod_optimum'
      ? { key: 'opt', label: BASIS_LABELS.tod_optimum, data: optimum }
      : null,
    regulatory && chosen !== 'regulatory_sizing'
      ? { key: 'reg', label: BASIS_LABELS.regulatory_sizing, data: regulatory }
      : null,
  ].filter(Boolean);

  return (
    <Card className="rounded-2xl border-border/70 bg-card p-6" data-testid="sizing-options-card">
      <div className="mb-1 flex items-center gap-2">
        <BarChart3 className="h-5 w-5 text-primary" />
        <h3 className="font-display text-base font-semibold">How this size was chosen</h3>
      </div>
      <p className="mb-4 text-sm text-muted-foreground">{options.basis_note}</p>

      <div className="rounded-xl border border-primary/20 bg-accent/60 p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <Badge className="border border-primary/20 bg-card text-accent-foreground">
            {BASIS_LABELS[chosen] || chosen}
          </Badge>
          <span className="font-display text-lg font-semibold tabular">
            {formatINR(active.net_annual_savings_rs, { decimals: 0 })} / yr
          </span>
        </div>
        <div className="mt-2 text-sm text-foreground tabular">
          {formatNumber(active.solar_kwp_dc)} kWp DC · {formatNumber(active.solar_kw_ac)} kW AC
          {active.battery_kwh > 0
            ? ` · ${formatNumber(active.battery_kwh)} kWh battery`
            : ' · no battery'}
        </div>
      </div>

      {rows.length > 0 && (
        <div className="mt-4">
          <div className="mb-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Where the monthly saving comes from
          </div>
          <div className="divide-y divide-border/60">
            {rows.map((r) => (
              <div
                key={r.key}
                className="flex items-center justify-between py-1.5 text-sm"
                data-testid={`savings-line-${r.key}`}
              >
                <span className="text-muted-foreground">{r.label}</span>
                <span
                  className={`font-medium tabular ${
                    r.value < 0 ? 'text-destructive' : 'text-foreground'
                  }`}
                >
                  {formatINR(r.value, { decimals: 0 })}
                </span>
              </div>
            ))}
            <div className="flex items-center justify-between pt-2 text-sm font-semibold">
              <span>Net saving per month</span>
              <span className="tabular">
                {formatINR(active.net_monthly_savings_rs, { decimals: 0 })}
              </span>
            </div>
          </div>
        </div>
      )}

      {alternatives.length > 0 && (
        <div className="mt-4 border-t border-border/60 pt-3">
          <div className="mb-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Also considered
          </div>
          {alternatives.map((alt) => (
            <div
              key={alt.key}
              className="flex flex-wrap items-baseline justify-between gap-2 py-1 text-sm"
              data-testid={`alternative-${alt.key}`}
            >
              <span className="text-muted-foreground">{alt.label}</span>
              <span className="tabular text-muted-foreground">
                {formatNumber(alt.data.solar_kwp_dc)} kWp
                {alt.data.battery_kwh > 0 ? ` + ${formatNumber(alt.data.battery_kwh)} kWh` : ''}
                {' · '}
                {formatINR(alt.data.net_annual_savings_rs, { decimals: 0 })} / yr
              </span>
            </div>
          ))}
        </div>
      )}

      {analysis.annualisation_note && (
        <p className="mt-4 text-xs leading-relaxed text-muted-foreground">
          {analysis.annualisation_note}
        </p>
      )}
    </Card>
  );
};

const StorageMandateNote = ({ bess }) => {
  const req = bess?.requirement;
  if (!req || !req.citation) return null;
  return (
    <div
      className="mt-3 rounded-xl border border-border/70 bg-secondary/60 p-3 text-xs leading-relaxed text-muted-foreground"
      data-testid="storage-mandate-note"
    >
      <span className="font-semibold text-foreground">
        {req.mandated ? 'Required by regulation' : 'Not required by regulation'}
      </span>
      {' — '}
      {req.citation}
      {req.unconfirmed && (
        <span className="mt-1 block text-amber-700">
          We have not yet confirmed this state&apos;s storage rule, so treat the battery size as
          indicative.
        </span>
      )}
    </div>
  );
};

const SolarTodBreakdown = ({ extraction, analysis }) => {
  const resolvedSlots = analysis?.resolved_slots || {};
  const cfg = analysis?.tod_slots_config || {};
  const rows = (extraction?.tod_slots || []).map((s) => {
    const cfgKey = `slot_${s.slot_number}`;
    const meta = cfg[cfgKey] || {};
    const resolved = resolvedSlots[s.slot_number] || {};
    return {
      slot_number: s.slot_number,
      slot_label: s.slot_label || meta.label || `Slot ${s.slot_number}`,
      window: `${s.start_time || meta.start || ''} – ${s.end_time || meta.end || ''}`,
      grid_import: Number(s.grid_import_kwh ?? resolved.grid_import_kwh ?? s.consumption_kwh ?? 0),
      solar_generation: Number(s.solar_generation_kwh ?? resolved.solar_generation_kwh ?? 0),
      solar_export: Number(s.solar_export_kwh ?? resolved.solar_export_kwh ?? 0),
      type: meta.type || 'normal',
    };
  });
  const anySolar = rows.some((r) => r.solar_export > 0 || r.solar_generation > 0);
  if (!anySolar) return null;

  return (
    <Card
      className="rounded-2xl border-border/70 bg-card p-6"
      data-testid="solar-tod-breakdown-table"
    >
      <div className="mb-3 flex items-center gap-2">
        <Sunrise className="h-5 w-5 text-primary" />
        <h3 className="font-display text-base font-semibold">Solar &amp; grid — TOD breakdown</h3>
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Slot</TableHead>
            <TableHead>Window</TableHead>
            <TableHead className="text-right">Grid import</TableHead>
            <TableHead className="text-right">Solar generated</TableHead>
            <TableHead className="text-right">Solar exported</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={`solar-tod-${r.slot_number}`}>
              <TableCell>
                <div className="flex items-center gap-2">
                  <span
                    className="h-2.5 w-2.5 rounded-full"
                    style={{ background: slotColor(r.type) }}
                  />
                  <span className="font-medium">{r.slot_label}</span>
                </div>
              </TableCell>
              <TableCell className="text-muted-foreground tabular">{r.window}</TableCell>
              <TableCell className="text-right font-medium tabular">
                {formatKWh(r.grid_import)}
              </TableCell>
              <TableCell className="text-right tabular text-muted-foreground">
                {r.solar_generation ? formatKWh(r.solar_generation) : '—'}
              </TableCell>
              <TableCell className="text-right tabular text-primary">
                {r.solar_export ? formatKWh(r.solar_export) : '—'}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
};

const ScenarioOptionCard = ({ title, subtitle, data, idx, testid, tone = 'primary' }) => {
  if (!data) return null;
  if (!data.applicable) {
    return (
      <Card className="rounded-2xl border-border/70 bg-card p-6" data-testid={testid}>
        <div className="mb-2 flex items-center gap-2">
          <span className="flex h-6 w-6 items-center justify-center rounded-full bg-muted text-xs font-semibold text-muted-foreground">
            {idx}
          </span>
          <h3 className="font-display text-base font-semibold">{title}</h3>
        </div>
        <p className="text-sm text-muted-foreground">{data.reason || subtitle}</p>
      </Card>
    );
  }
  const highlight = tone === 'primary' ? 'border-primary/25 shadow-[0_10px_28px_rgba(29,78,216,0.10)]'
    : 'border-border/70';
  return (
    <Card className={`rounded-2xl bg-card p-6 ${highlight}`} data-testid={testid}>
      <div className="mb-2 flex items-center gap-2">
        <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-xs font-semibold text-primary-foreground">
          {idx}
        </span>
        <h3 className="font-display text-base font-semibold">{title}</h3>
      </div>
      <p className="text-sm text-muted-foreground">{data.label || subtitle}</p>

      <div className="mt-4 grid grid-cols-2 gap-4">
        {data.additional_solar_kwp_dc !== undefined && (
          <>
            <Kpi label="Additional solar" value={`${data.additional_solar_kwp_dc} kWp DC`} sub={`${data.additional_solar_kw_ac} kW AC`} />
            <Kpi label="Battery" value={`${data.battery_kwh} kWh`} sub="peak-slot shift" />
          </>
        )}
        {data.additional_solar_kwp_dc === undefined && (
          <>
            <Kpi label="Battery" value={`${data.battery_kwh} kWh`} sub={data.daily_solar_export_slot3_kwh ? `${data.daily_solar_export_slot3_kwh.toFixed(0)} kWh/day` : ''} />
            <Kpi label="Slot 3 export shifted" value={formatKWh(data.solar_export_slot3_month_kwh || 0)} sub="→ Slot 4 peak" />
          </>
        )}
      </div>

      <div className="mt-4 space-y-1 border-t border-border/60 pt-4 text-sm">
        {data.slot_4_grid_target_kwh !== undefined && (
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Slot 4 grid units offset</span>
            <span className="font-medium tabular">{formatKWh(data.slot_4_grid_target_kwh)}</span>
          </div>
        )}
        {data.monthly_peak_saving_rs !== undefined && (
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Monthly peak-slot saving</span>
            <span className="font-medium tabular">{formatINR(data.monthly_peak_saving_rs)}</span>
          </div>
        )}
        {data.monthly_surplus_income_rs !== undefined && (
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Monthly surplus export income</span>
            <span className="font-medium tabular">{formatINR(data.monthly_surplus_income_rs)}</span>
          </div>
        )}
        {data.monthly_fixed_demand_charge_rs !== undefined && data.monthly_fixed_demand_charge_rs > 0 && (
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Extra contract-demand charge ({data.added_contract_demand_kva} kVA)</span>
            <span className="font-medium tabular text-destructive">
              -{formatINR(data.monthly_fixed_demand_charge_rs)}
            </span>
          </div>
        )}
        {data.peak_revenue_monthly_rs !== undefined && (
          <>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Peak revenue (RTE × export × peak)</span>
              <span className="font-medium tabular">{formatINR(data.peak_revenue_monthly_rs)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Export income forgone</span>
              <span className="font-medium tabular text-destructive">
                -{formatINR(data.export_income_forgone_monthly_rs)}
              </span>
            </div>
          </>
        )}
        <div className="mt-3 flex items-center justify-between border-t border-border/60 pt-3">
          <span className="text-sm text-muted-foreground">Annual savings</span>
          <span className="text-lg font-semibold tabular text-primary">
            {formatINR(data.annual_savings_rs)}
          </span>
        </div>
        {data.note && (
          <p className="mt-2 text-xs text-muted-foreground">{data.note}</p>
        )}
      </div>
    </Card>
  );
};

const Kpi = ({ label, value, sub }) => (
  <div>
    <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
    <div className="mt-1 font-display text-xl font-semibold tabular">{value}</div>
    {sub && <div className="text-xs text-muted-foreground">{sub}</div>}
  </div>
);

const SolarScenariosSection = ({ scenarios }) => {
  if (!scenarios) return null;
  const o1 = scenarios.option_1_bess_only;
  const o2 = scenarios.option_2_full_offset;
  const o3 = scenarios.option_3_min_reg;
  return (
    <div className="space-y-4" data-testid="solar-scenarios-section">
      <div>
        <div className="text-xs font-semibold uppercase tracking-wider text-primary">Existing rooftop solar detected</div>
        <h2 className="mt-1 font-display text-xl font-semibold text-foreground">
          You already have {scenarios.existing_solar_kwp} kWp solar — here are 3 investment options
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Grid support charge applied on generated solar units: <span className="font-medium">
            {formatINR(scenarios.grid_support_charge_rs_per_kwh, { decimals: 2 })}/kWh
          </span>
        </p>
      </div>
      <div className="grid gap-4 lg:grid-cols-3">
        <ScenarioOptionCard
          idx={1}
          title="Option 1 — Battery only"
          subtitle="Store your Slot-3 solar export and use it during Slot-4 peak."
          data={o1}
          testid="scenario-option-1"
        />
        <ScenarioOptionCard
          idx={2}
          title="Option 2 — Fully offset Slot 4"
          subtitle="Add solar + a large battery sized to fully eliminate Slot-4 grid consumption."
          data={o2}
          testid="scenario-option-2"
        />
        <ScenarioOptionCard
          idx={3}
          title="Option 3 — Regulation minimum"
          subtitle="Battery = Solar AC (1:1). Excess Slot-3 exports at surplus rate; extra contract demand cost subtracted."
          data={o3}
          testid="scenario-option-3"
          tone="secondary"
        />
      </div>
    </div>
  );
};

export const ResultsState = ({ submission, onReset }) => {
  const analysis = submission?.analysis || {};
  const extraction = submission?.extraction || {};
  const solar = analysis.solar;
  const bess = analysis.bess;
  const total = analysis.total_annual_savings_rs || 0;
  const scenarios = analysis.solar_scenarios;
  const hasExistingSolar = (analysis.existing_solar_kwp || extraction.existing_solar_kwp || 0) > 0;
  const rainyWarning = analysis.rainy_month_warning;
  const basis = analysis.options?.recommended;
  const req = bess?.requirement;
  const mandateMinKwh = req?.mandated ? Number(req.required_kwh || 0) : 0;
  const exceedsMandate = mandateMinKwh > 0 && Number(bess?.kwh_capacity || 0) > mandateMinKwh + 0.5;
  const batteryBasisNote = req?.mandated
    ? `${analysis.state || 'This state'} requires at least ${formatNumber(
        req.ratio_kwh_per_kw ?? 1,
        2,
      )} kWh per kW of solar above ${formatNumber(req.threshold_kw ?? 0)} kW — ${formatNumber(
        mandateMinKwh,
      )} kWh here.${
        exceedsMandate
          ? ` We recommend ${formatNumber(
              bess.kwh_capacity,
            )} kWh, above the minimum, because your peak slots can use it.`
          : ''
      }`
    : bess?.kwh_capacity > 0
      ? 'Battery sized to shift the units sitting in your dearest TOD slots, capped by what those slots actually use.'
      : 'No battery proposed for this bill.';

  return (
    <motion.div
      initial={RESULTS_MOTION_INITIAL}
      animate={RESULTS_MOTION_ANIMATE}
      transition={RESULTS_MOTION_TRANSITION}
      className="mx-auto max-w-6xl px-4 py-10 sm:px-6 lg:px-8"
      data-testid="results-screen"
    >
      <div className="mb-6 flex flex-col justify-between gap-2 sm:flex-row sm:items-end">
        <div>
          <div className="flex flex-wrap items-center gap-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
            <MapPin className="h-3.5 w-3.5" /> {analysis.state || '-'} · {analysis.consumer_type || '-'} · {extraction.discom || extraction.utility_name || 'DISCOM'}
          </div>
          <h1 className="mt-1 font-display text-2xl font-semibold text-foreground sm:text-3xl">
            {extraction.consumer_name || submission?.customer?.company_name || 'Your'} — Bill Analysis
          </h1>
          {submission?.customer?.person_name && (
            <p className="text-sm text-muted-foreground">
              Prepared for <span className="font-medium text-foreground">{submission.customer.person_name}</span>
              {submission?.customer?.company_name ? ` · ${submission.customer.company_name}` : ''}
            </p>
          )}
          <p className="mt-1 text-sm text-muted-foreground">
            Billing month {extraction.billing_month || '—'} · {extraction.billing_days || '—'} days · Total {formatKWh(extraction.total_consumption_kwh || 0)}
          </p>
        </div>
        <Button
          variant="outline"
          className="rounded-xl transition-colors duration-200"
          onClick={onReset}
          data-testid="upload-another-bill-button"
        >
          <RefreshCcw className="mr-2 h-4 w-4" /> Upload another bill
        </Button>
      </div>

      <div className="space-y-6">
        {rainyWarning && <RainyMonthBanner note={analysis.rainy_month_note} />}
        <KpiHero value={total} submission={submission} />
        {hasExistingSolar && scenarios && <SolarScenariosSection scenarios={scenarios} />}
        {!hasExistingSolar && <SizingOptions analysis={analysis} />}

        <div className="grid gap-6 lg:grid-cols-12">
          <div className="space-y-6 lg:col-span-7">
            <TodConsumptionTable extraction={extraction} analysis={analysis} />
            <SolarTodBreakdown extraction={extraction} analysis={analysis} />
            <TodChart extraction={extraction} analysis={analysis} />
            <BillSummary extraction={extraction} analysis={analysis} />
            <TariffTable tariff={extraction.tariff_components} computed={analysis.tariff} />
          </div>
          <div className="space-y-6 lg:col-span-5">
            {!hasExistingSolar && <SolarCard solar={solar} existingSolar={extraction.existing_solar_kwp} />}
            {!hasExistingSolar && (
              <>
                <BessCard bess={bess} />
                <StorageMandateNote bess={bess} />
              </>
            )}
            <Card className="rounded-2xl border-border/70 bg-accent/60 p-6" data-testid="assumptions-card">
              <div className="mb-2 flex items-center gap-2">
                <Zap className="h-5 w-5 text-primary" />
                <h3 className="font-display text-base font-semibold">Sizing methodology</h3>
              </div>
              <ul className="space-y-1.5 text-sm text-muted-foreground">
                <li>
                  1 kWp DC solar generates ~
                  {formatNumber(analysis.assumptions?.solar_units_per_kwp_per_day || 5, 2)} units/day in{' '}
                  {analysis.state || 'your state'}.
                </li>
                <li>
                  Inverter AC = DC /{' '}
                  {formatNumber(analysis.assumptions?.ac_dc_ratio || 1.3, 2)}, capped at what your
                  connection can take.
                </li>
                {basis === 'tod_optimum' ? (
                  <li>
                    Solar and battery sized together by simulating every TOD slot and keeping the
                    combination with the highest net saving.
                  </li>
                ) : (
                  <li>
                    Solar sized to offset the solar-day slot
                    {analysis.assumptions?.battery_uplift_applied
                      ? `, then increased by ${Math.round(
                          ((analysis.assumptions?.battery_charging_solar_uplift || 1.2) - 1) * 100,
                        )}% to charge the battery`
                      : ''}
                    .
                  </li>
                )}
                <li>{batteryBasisNote}</li>
                <li>Battery round-trip efficiency: {((analysis.bess?.rte || 0.9) * 100).toFixed(0)}%.</li>
                {hasExistingSolar && (
                  <li>Grid support charge applied on rooftop solar units (MSEDCL): ₹1.42/kWh (HT), ₹1.96/kWh (LT).</li>
                )}
                <li className="pt-2 text-foreground">
                  Cost &amp; ROI are provided separately by the UrjaPod team based on your specific site.
                </li>
              </ul>
            </Card>

            <Button
              variant="outline"
              size="lg"
              className="w-full rounded-xl"
              data-testid="download-report-button"
              onClick={() => window.print()}
            >
              <Download className="mr-2 h-4 w-4" />
              Download this analysis as PDF
            </Button>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default ResultsState;
